
import React, { useEffect, useState } from 'react';
import { Grid, Box, Divider, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { useRouteMatch } from 'react-router';

import { AnalyticCard } from 'src/scenes/Project/components';
import { AutocompleteWrapper, ChartWrapper } from 'src/components';
import { ScrollContainer } from 'react-indiana-drag-scroll';
import { useFetchStatus } from 'src/hooks';
import { buildChartData, chartDefaultOptions, htmlLegendPlugin } from 'src/scenes/Project/scenes/Analytics/smaAnalyticsHelper';
import { removeUndefinedPropsFromObject as rupfo, deepObjClone } from 'src/utils/util';
import { useMonitoringPlanApi } from 'src/services';
import { LoadingBox, NoProcessBox } from 'src/scenes/Project/scenes/Dashboard/components';


const useStyles = makeStyles(theme => ({
  scrollbar: {
    '&::-webkit-scrollbar': {
      width: theme.spacing(1),
    },
    '&::-webkit-scrollbar-track': {
      borderRadius: '6px',
      backgroundColor: theme.palette.common.gray300,
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: '6px',
      backgroundColor: '#555',
    },
  },
  chartGroup: {
    borderRadius: theme.shape.borderRadius,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.palette.common.gray400,
  },
  subtitle: {
    fontWeight: 500,
    textTransform: 'uppercase',
    color: theme.palette.common.gray600,
    lineHeight: 1.5,
  },
}));

const MonitoringPlanAnalytics = () => {
  const classes = useStyles();
  const match = useRouteMatch();
  const monitoringPlanApi = useMonitoringPlanApi();

  const [ analyticsCharts, setAnalyticsCharts ] = useState([]);
  const [ selectedMonitoringPlan, setSelectedMonitoringPlan ] = useState();
  const [ monitoringPlanOptions, setMonitoringPlanOptions ] = useState([]);

  let { projectId, siteId } = match.params;
  const isSite = Boolean(siteId);
  projectId = isSite ? siteId : projectId;

  const componentId = match.url.split('/').filter(Boolean).pop();

  const getAnalyticsCharts = async () => {
    setAnalyticsCharts([]);
    const [ analyticsData, chartTypes ] = await Promise.all([
      monitoringPlanApi.getAnalyticsData(rupfo({ monitoringPlanId: selectedMonitoringPlan.value, componentId })),
      monitoringPlanApi.getChartTypes(selectedMonitoringPlan.value),
    ]);

    const chartTypeOptions = chartTypes.map(cto => ({ label: cto.name, value: cto.id }));

    const analyticsCharts = analyticsData.map(analytic => {
      const { name: title } = analytic;
      const { separatedKey, separatedInfo, isStacked, chartJsType = 'bar', sort, isLogarithmic,
        valueToChart, valuesToChart } = analytic.config;
      const fixedValToChart = valueToChart ? [ valueToChart ] : valuesToChart;
      const chart = buildChartData({
        valuesToChart: fixedValToChart,
        separatedKey,
        separatedInfo,
        dataToChart: analytic.data,
        hasSomeSeparatedData: chartTypeOptions.map(cto => cto.value).includes(separatedKey) || fixedValToChart.length > 1,
        sortType: sort,
      });

      const chartOptions = deepObjClone(chartDefaultOptions);
      chartOptions.scales.x.stacked = isStacked;
      chartOptions.scales.y = { stacked: isStacked, ...(isLogarithmic ? { type: 'logarithmic' } : {}) };
      chartOptions.plugins.htmlLegend = { containerId: analytic.id };
      chartOptions.maintainAspectRatio = false;
      chartOptions.responsive = true;

      return { id: analytic.id, title, chart, options: chartOptions, chartJsType };
    });
    setAnalyticsCharts(analyticsCharts);
  };

  const { isLoading: isLoadingCharts, isSuccess: isSuccessCharts, handleConfirm: fetchAnalyticsCharts } = useFetchStatus({
    confirmFn: getAnalyticsCharts,
  });

  useEffect(() => {
    if (selectedMonitoringPlan?.value) {
      fetchAnalyticsCharts();
    }
  }, [ selectedMonitoringPlan?.value ]); // eslint-disable-line react-hooks/exhaustive-deps

  const getMonitoringPlans = async () => {
    const { monitoringPlanList } = await monitoringPlanApi.getMonitoringPlans({ folderId: projectId, size: 'all' });
    const monitoringPlanOptions = monitoringPlanList.filter(mp => mp.nCharts > 0).map(mp => ({ label: mp.name, value: mp.id, data: mp }));
    setMonitoringPlanOptions(monitoringPlanOptions);
    if (monitoringPlanList.length > 0) {
      const firstMonitoringWithCharts = monitoringPlanList.find(mp => mp.nCharts > 0);
      setSelectedMonitoringPlan({ label: firstMonitoringWithCharts?.name, value: firstMonitoringWithCharts?.id });
    }
  };

  const { handleConfirm: fetchMonitoringPlans, isLoading: isLoadingMonitoringPlans } = useFetchStatus({ confirmFn: getMonitoringPlans });

  useEffect(() => {
    fetchMonitoringPlans();
  // eslint-disable-next-line
  }, []);

  const hasSomeAnalytic = monitoringPlanOptions.some(option => option.data.nCharts > 0);
  const hasAnalyticsInSelectedPlan = analyticsCharts.length > 0;
  const hasLegend = chart => Array.isArray(chart?.datasets) && chart.datasets.length > 1;

  return (
    (isLoadingMonitoringPlans || isLoadingCharts || hasSomeAnalytic) &&
      <Grid item xs={12}>
        <AnalyticCard
          title="Analíticas de plan de monitoreo"
          ActionComponent={
            () =>
              <Box width={200}>
                <AutocompleteWrapper
                  placeholder="Plan de monitoreo"
                  value={{ label: selectedMonitoringPlan?.label, value: selectedMonitoringPlan?.value } || { label: '', value: '' }}
                  options={monitoringPlanOptions}
                  onChange={(_, option) => setSelectedMonitoringPlan(option)}
                  size="very-small"
                  getOptionSelected={(option, value) => (option.value === value.value || value.value === '')}
                  clearable
                  fullWidth
                  fixEmptyWarn
                />
              </Box>
          }
        >
          <Divider />
          <Grid item container spacing={2}>
            {(isLoadingCharts || isLoadingMonitoringPlans) && <LoadingBox label='analíticas de plan de monitoreo' />}
            {(isSuccessCharts && !hasAnalyticsInSelectedPlan) &&
              <NoProcessBox label='No se encontraron analíticas en el plan de monitoreo seleccionado' />
            }
            {(isSuccessCharts && hasAnalyticsInSelectedPlan) &&
              analyticsCharts.map(ac =>
                <Grid key={`ac-${ac.id}`} item xs={6}>
                  <Box p={2} display="flex" flexDirection="column">
                    <Box minHeight={40} alignContent="center">
                      <Typography variant="body2" className={classes.subtitle}>{ac.title}</Typography>
                    </Box>
                    <Box
                      className={classes.chartGroup}
                      display={ac.chart.datasets.length === 1 ? 'flex' : 'block'}
                      alignItems="flex-end"
                      p={2}
                    >
                      <ScrollContainer hideScrollbars={false} className={classes.scrollbar}>
                        <Box id={ac.id} maxHeight={80} />
                      </ScrollContainer>
                      <Box height={hasLegend(ac.chart) ? 280 : 360} width="100%">
                        <ChartWrapper type={ac.chartJsType} options={ac.options} data={ac.chart} plugins={[ htmlLegendPlugin ]} />
                      </Box>
                    </Box>
                  </Box>
                </Grid>,
              )
            }
          </Grid>
        </AnalyticCard>
      </Grid>
  );
};


export {
  MonitoringPlanAnalytics,
};